import { Box } from "@mui/system"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { useMediaQuery } from "react-responsive"
import { useRecoilState } from "recoil"
import { TableHeaderItem } from "src/components/mypage/TableHeaderItem"
import { useLoginContext } from "src/hooks/useLoginContext"
import {
  APPLICATION_STATUS,
  CONSULT_STATUS,
  JOIN_TYPE,
  MEMBER_TYPE_STRING_CAPITAL,
  RESPONSE_CODE,
  SNACKBAR_TYPES,
} from "src/modules/const"
import { Event, EventService, UserApplication } from "src/openapi"
import { EventItem } from "src/pages/event/components/EventItem"
import { ShowProgress } from "src/states/showProgress"
import { ShowToast } from "src/states/showToast"
import { Button, Grid, Typography } from "src/UILibrary"
import { ApplicationStatusLabel } from "../event/ApplicationStatusLabel"
import { EventJoinCancelModal } from "./EventJoinCancelModal"
import { EventTableItem } from "./EventTableItem"

type Props = {
  item: UserApplication
  handleItem: any
  closeDetail: any
}

export const EventUserDetailPage = ({ item, handleItem, closeDetail }: Props) => {
  const { t } = useTranslation("translation", { keyPrefix: "mypage.inbox" })

  const { accessToken, getAreaDetail, checkLoginStatus } = useLoginContext()

  const [, setShowToast] = useRecoilState(ShowToast)
  const [, setShowProgress] = useRecoilState(ShowProgress)

  const [openCancelModal, setOpenCancelModal] = useState<boolean>(false)

  let headers: any[] = [
    {
      size: 2,
      name: t("date"),
      sortby: "",
    },
    {
      size: 1.5,
      name: t("status"),
      sortby: "",
    },
    {
      size: 1.5,
      name: t("author"),
      sortby: "",
    },
    {
      size: 5,
      name: t("event_title"),
      sortby: "",
    },
    {
      size: 2,
      name: t("event_date"),
      sortby: "",
    },
  ]

  const handleRejoin = () => {
    setShowProgress(true)
    EventService.updateEventApplication(accessToken, item.event?.id as number, item.id as number, {
      status: APPLICATION_STATUS.RESERVED,
    })
      .then((res) => {
        setShowProgress(false)
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.SUCCESS) {
          setShowToast({ show: true, text: t("success_join"), type: SNACKBAR_TYPES.SUCCESS })

          let tmpItem = { ...item }
          tmpItem.status = res.data?.application?.status
          tmpItem.updatedAt = res.data?.application?.updatedAt
          handleItem(tmpItem)
        } else {
          setShowToast({ show: true, text: t("failed_join"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed_join"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const isTablet = useMediaQuery({ query: "(min-width: 1200px)" })

  return (
    <Box
      sx={{
        ...(isTablet
          ? { height: "auto", pt: "0px", pl: "20px" }
          : { width: "100%", position: "absolute", top: 60, bottom: 120 }),
        overflowY: "scroll",
      }}
    >
      <Typography.FileFieldDetail
        sx={{
          fontFamily: "Noto Sans JP !important",
          my: "12px",
          ml: "20px",
          ":hover": { opacity: 0.8, cursor: "pointer" },
        }}
        onClick={() => closeDetail()}
      >
        {"＜　"}
        {t("page_back")}
      </Typography.FileFieldDetail>
      {isTablet ? (
        <Grid container sx={{ mt: "0px", height: "32px", borderBottom: "1px dotted #D4D3D7" }}>
          {headers.map((header, index) => (
            <TableHeaderItem
              key={index}
              name={header.name}
              size={header.size}
              sortby={header.sortby}
            />
          ))}
        </Grid>
      ) : (
        <></>
      )}
      <EventTableItem item={item} handleSelected={() => {}} />
      <Box sx={{ px: "20px", pt: "20px", display: "flex" }}>
        <Typography.DetailText
          sx={{ fontFamily: "Noto Sans JP !important", color: "#C69989", mr: "20px" }}
        >
          {t("status")}
        </Typography.DetailText>
        {item.status === APPLICATION_STATUS.RESERVED && (
          <>
            <Typography.DetailText
              sx={{ fontFamily: "Noto Sans JP !important", color: "#C69989", mr: "10px" }}
            >
              {item.createdAt?.slice(0, 10)}
            </Typography.DetailText>
            <ApplicationStatusLabel
              isBehalf={item.userType === MEMBER_TYPE_STRING_CAPITAL.ADMINISTRATOR}
              status={item.status as string}
            />
          </>
        )}
        {item.status === CONSULT_STATUS.CANCELED && (
          <>
            <Typography.DetailText
              sx={{ fontFamily: "Noto Sans JP !important", color: "#C69989", mr: "10px" }}
            >
              {item.createdAt?.slice(0, 10)}
            </Typography.DetailText>
            <ApplicationStatusLabel
              isBehalf={item.userType === MEMBER_TYPE_STRING_CAPITAL.ADMINISTRATOR}
              status={APPLICATION_STATUS.RESERVED}
            />
            <Typography.DetailText
              sx={{ fontFamily: "Noto Sans JP !important", color: "#D4D3D7", mx: "10px" }}
            >
              {">"}
            </Typography.DetailText>
            <>
              <Typography.DetailText
                sx={{ fontFamily: "Noto Sans JP !important", color: "#C69989", mr: "10px" }}
              >
                {item.updatedAt?.slice(0, 10)}
              </Typography.DetailText>
              <ApplicationStatusLabel
                isBehalf={item.userType === MEMBER_TYPE_STRING_CAPITAL.ADMINISTRATOR}
                status={item.status as string}
              />
            </>
          </>
        )}
      </Box>
      {item.status === APPLICATION_STATUS.RESERVED && (
        <>
          <Box sx={{ mt: "20px", ml: "20px" }}>
            <Typography.InputFieldTitle
              sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700" }}
            >
              {t("event_applicated")}
            </Typography.InputFieldTitle>
            <Typography.InputFieldTitle sx={{ fontFamily: "Noto Sans JP !important", mt: "20px" }}>
              {t("event_applicated_content")}
            </Typography.InputFieldTitle>
            <Typography.InputFieldTitle
              sx={{
                fontFamily: "Noto Sans JP !important",
                mt: "20px",
                color: "#998E8A",
                textDecoration: "underline",
                ":hover": { opacity: 0.8, cursor: "pointer" },
                width: "fit-content",
              }}
              onClick={() => setOpenCancelModal(true)}
            >
              {t("cancel_event")}
            </Typography.InputFieldTitle>
          </Box>
          <Box sx={{ ml: "20px" }}>
            <Typography.InputFieldTitle
              sx={{
                fontFamily: "Noto Sans JP !important",
                fontWeight: "700",
                pb: "10px",
                mt: "30px",
                borderBottom: "1px dashed #D4D3D7",
                width: "100%",
              }}
            >
              {t("event_information_confirm")}
            </Typography.InputFieldTitle>
            <Typography.InputFieldTitle
              sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", mt: "20px" }}
            >
              {t("event_date")}
            </Typography.InputFieldTitle>
            <Typography.DetailText
              sx={{
                fontFamily: "Noto Sans JP !important",
                mt: "12px",
              }}
            >
              {`${item.event?.startTime?.slice(0, 16).replace("T", " ")} - ${item.event?.finishTime
                ?.slice(0, 16)
                .replace("T", " ")}`}
            </Typography.DetailText>
            {item.type === JOIN_TYPE.ONLINE ? (
              <>
                <Typography.InputFieldTitle
                  sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", mt: "20px" }}
                >
                  {t("event_zoom_link")}
                </Typography.InputFieldTitle>
                <Typography.DetailText
                  sx={{
                    fontFamily: "Noto Sans JP !important",
                    mt: "12px",
                  }}
                >
                  {t("event_zoom_description")}
                </Typography.DetailText>
                <Typography.DetailText
                  sx={{
                    fontFamily: "Noto Sans JP !important",
                    mt: "12px",
                    fontSize: "12px",
                    fontWeight: "700",
                    color: "#235496",
                    ":hover": { opacity: 0.8, cursor: "pointer" },
                  }}
                  onClick={() => window.open(item.event?.linkUrl, "_blank")}
                >
                  {item.event?.linkUrl}
                </Typography.DetailText>
              </>
            ) : (
              <>
                <Typography.InputFieldTitle
                  sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", mt: "20px" }}
                >
                  {t("event_place")}
                </Typography.InputFieldTitle>
                <Box display="flex" sx={{ mt: "12px" }}>
                  <Box sx={{ width: "320px", height: "160px", overflow: "hidden" }}>
                    <iframe
                      src={`https://maps.google.com/maps?q=${
                        getAreaDetail(item.event?.areaId as number).facilityLatitude
                      },${
                        getAreaDetail(item.event?.areaId as number).facilityLongitude
                      }&hl=ja&z=14&output=embed`}
                      style={{ border: 0, width: "100%", height: "320px", marginTop: "-80px" }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    ></iframe>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", mx: "10px", my: "auto" }}>
                    <Typography.DetailText
                      sx={{
                        fontFamily: "Noto Sans JP !important",
                        mt: "12px",
                      }}
                    >
                      {getAreaDetail(item.event?.areaId as number).facilityAddress}
                    </Typography.DetailText>
                    <Typography.DetailText
                      sx={{
                        fontFamily: "Noto Sans JP !important",
                        mt: "12px",
                        fontSize: "12px",
                        fontWeight: "700",
                        color: "#235496",
                        ":hover": { opacity: 0.8, cursor: "pointer" },
                      }}
                      onClick={() =>
                        window.open(
                          `https://www.google.com/maps/search/?api=1&query=${
                            getAreaDetail(item.event?.areaId as number).facilityLatitude
                          },${getAreaDetail(item.event?.areaId as number).facilityLongitude}`,
                          "_blank"
                        )
                      }
                    >
                      {t("to_google_map")}
                    </Typography.DetailText>
                  </Box>
                </Box>
              </>
            )}
            <Typography.InputFieldTitle
              sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", mt: "20px" }}
            >
              {t("event_join_deadline")}
            </Typography.InputFieldTitle>
            <Typography.DetailText
              sx={{
                fontFamily: "Noto Sans JP !important",
                mt: "12px",
              }}
            >
              {`${item.event?.startTime?.slice(0, 16).replace("T", " ")} - ${item.event?.finishTime
                ?.slice(0, 16)
                .replace("T", " ")}`}
            </Typography.DetailText>
          </Box>
        </>
      )}
      {item.status === APPLICATION_STATUS.CANCELLED && (
        <Box sx={{ p: "20px" }}>
          <Typography.InputFieldTitle
            sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", mt: "10px" }}
          >
            {t("event_is_cancelled")}
          </Typography.InputFieldTitle>
          <Typography.InputFieldTitle
            sx={{
              fontFamily: "Noto Sans JP !important",
              fontSize: "400",
              mt: "20px",
            }}
          >
            {item.rejectReason}
          </Typography.InputFieldTitle>
          <Button
            variant="contained"
            sx={{
              minWidth: "240px",
              py: "10px",
              fontSize: "1rem",
              fontFamily: "Noto Sans JP !important",
              lineHeight: "1rem",
              fontWeight: 700,
              mt: "20px",
              display: "flex",
              justifyContent: "center",
              borderRadius: 0,
              ":hover": {
                bgcolor: "#2DA389",
              },
            }}
            onClick={() => handleRejoin()}
          >
            {t("rejoin")}
          </Button>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          width: "100%",
          px: "20px",
          py: "10px",
          mt: "20px",
        }}
      >
        <EventItem item={item.event as Event} handleSelected={() => {}} showFavorite />
      </Box>
      <EventJoinCancelModal
        item={item}
        handleItem={handleItem}
        handleOpen={setOpenCancelModal}
        open={openCancelModal}
      />
    </Box>
  )
}
